import { useEffect, useRef, useCallback } from "react";
import { useSocket as useSocketContext } from "context/SocketProvider";

/**
 * useSocket
 *
 * Lightweight wrapper around the SocketProvider's API that:
 * - Provides the raw socket helpers (socket, emit, on, off, connected)
 * - Adds a `subscribe(event, handler)` helper that automatically unregisters
 *   handlers when the component unmounts.
 *
 * Usage:
 * const { socket, emit, subscribe, connected } = useSocket();
 * useEffect(() => {
 *   const cleanup = subscribe('appointment.status.updated', handler);
 *   return cleanup; // optional, it's also auto-cleaned on unmount
 * }, [subscribe]);
 */
export default function useSocket() {
  const { socket, emit, on, off, subscribe: ctxSubscribe, connected } =
    useSocketContext();

  // Track handlers registered via this hook so we can clean them up on unmount
  const handlersRef = useRef([]);

  useEffect(() => {
    return () => {
      // Cleanup all handlers registered through this hook on unmount
      try {
        handlersRef.current.forEach(({ event, handler }) => {
          try {
            if (off) off(event, handler);
          } catch (e) {
            // ignore individual cleanup errors
            // but keep cleaning the rest
            // console.warn("useSocket cleanup handler error", e);
          }
        });
      } finally {
        handlersRef.current = [];
      }
    };
    // We intentionally do not depend on `off` reference identity changing frequently.
    // If your provider replaces `off` dynamically, you may add it to deps.
  }, [off]);

  /**
   * subscribe
   * - Registers an event handler and returns an unsubscribe function.
   * - Also ensures the handler is removed automatically when the component unmounts.
   */
  const subscribe = useCallback(
    (event, handler) => {
      if (!ctxSubscribe) {
        console.warn("useSocket: subscribe called but socket not initialized");
        return () => {};
      }

      // Register using provider helper
      const unsubscribe = ctxSubscribe(event, handler);

      // Track for auto-cleanup
      handlersRef.current.push({ event, handler });

      // Return an unsubscribe function that removes handler and from registry
      return () => {
        try {
          if (off) off(event, handler);
        } catch (err) {
          // ignore individual off errors
        }
        handlersRef.current = handlersRef.current.filter(
          (h) => h.handler !== handler || h.event !== event,
        );
        // Also call provider returned unsubscribe if present
        try {
          if (typeof unsubscribe === "function") unsubscribe();
        } catch (e) {
          // ignore
        }
      };
    },
    [ctxSubscribe, off],
  );

  return {
    socket,
    emit,
    on,
    off,
    subscribe,
    connected,
  };
}
